import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Chip, Dropdown, DropdownItem, DropdownMenu, DropdownTrigger, Modal, ModalBody, ModalContent, ModalHeader, Select, SelectItem, Input, Tab, Tabs, Tooltip, useDisclosure } from '@nextui-org/react';
import { Icon } from '@iconify/react';
import { CtyButton, CtyShowToast, SimpleDeleteConfirmationModal, checkType } from '@circuitry-ai/doc-ui';
import { useTranslation } from 'react-i18next';
import {
  agentBuilderApi,
  useAgentBuilderAuth,
  BuilderApiError,
  type Blueprint,
  type Issue,
  type Preview,
  type Settings,
  type SyncStatus,
  type ToolInfo,
  type Workflow,
} from './agentBuilderApi';
import { JOURNEYS_PATH } from './AgentBuilderList';
import { BuilderChat } from './BuilderChat';
import { BuilderFlow } from './BuilderFlow';
import { NodeDetails, ToolsPanel } from './BuilderPanels';
import { RunDialog } from './BuilderRun';

// Agent builder: chat on the left (about 30%), the workflow it builds on the right.
// Every change goes through the builder service, which builds only from the agent platform's
// catalogue and keeps the workflow, the requirement and the agent's instructions in sync.
// backPath: the list this ASJ was opened from (the Journeys page, or the temporary preview).
export function AgentBuilderPage({ backPath = JOURNEYS_PATH }: { backPath?: string }) {
  useAgentBuilderAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { name = '' } = useParams();
  const [wf, setWf] = useState<Workflow | null>(null);
  const [tools, setTools] = useState<ToolInfo[]>([]);
  const [blueprint, setBlueprint] = useState<Blueprint | null>(null);
  const [profiles, setProfiles] = useState<string[]>([]);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [issues, setIssues] = useState<Issue[]>([]);
  const [sync, setSync] = useState<SyncStatus | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [busy, setBusy] = useState<null | 'chat' | 'publish' | 'save'>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [chatError, setChatError] = useState<string | null>(null);
  const [catalogueError, setCatalogueError] = useState<string | null>(null);
  const instructions = useDisclosure();
  const settingsDialog = useDisclosure();
  const runDialog = useDisclosure();
  const deleteDialog = useDisclosure();
  const [inUse, setInUse] = useState<{ agent_md: string; task_md: string } | null>(null);
  const [renaming, setRenaming] = useState<string | null>(null);
  const actionsDialog = useDisclosure();
  const [panelOpen, setPanelOpen] = useState(true);
  const [issuesOpen, setIssuesOpen] = useState(false);

  const refresh = useCallback(
    async (w: Workflow) => {
      setSync(await agentBuilderApi.sync(name).catch(() => null));
      if (w.nodes.length <= 1) {
        setPreview(null);
        setIssues([]);
        return;
      }
      try {
        setPreview(await agentBuilderApi.preview(name, w));
        setIssues([]);
      } catch (e) {
        setPreview(null);
        setIssues(e instanceof BuilderApiError ? e.issues : []);
      }
    },
    [name],
  );

  const adopt = useCallback(
    (w: Workflow) => {
      setWf(w);
      void refresh(w);
    },
    [refresh],
  );

  // The agent is all the page needs to open. Its blueprint (the actions it may use) and the LLM
  // profiles come from the agent platform's catalogue; if that is unavailable the agent is still
  // shown, and building is paused.
  useEffect(() => {
    const catalogueFailed = (e: Error) => setCatalogueError(e.message);
    const showTools = (toolList: ToolInfo[]) => {
      setTools(toolList);
      setCatalogueError(null);
    };
    agentBuilderApi.workflow(name).then(
      (w) => {
        adopt(w);
        // Every action the MCP server offers; a blueprint is the base, not a limit.
        if (w.blueprint) {
          agentBuilderApi.blueprint(w.blueprint).then(setBlueprint, catalogueFailed);
        }
        agentBuilderApi.tools().then(showTools, catalogueFailed);
      },
      (e: BuilderApiError) =>
        setLoadError(
          e.status === 404
            ? t('This agent does not exist, or it was written by hand and cannot be edited here.')
            : e.message,
        ),
    );
    agentBuilderApi.llmProfiles().then(setProfiles, () => undefined);
    // Not on `t`: reloading the workflow when the translations change would reset the canvas.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name, adopt]);

  // The instructions the agent really runs with, fetched fresh whenever the dialog opens.
  useEffect(() => {
    if (instructions.isOpen) agentBuilderApi.published(name).then(setInUse, () => setInUse(null));
  }, [instructions.isOpen, name]);

  const built = !!wf && wf.nodes.length > 1;
  const selectedNode = wf?.nodes.find((n) => n.id === selected) ?? null;
  const steps = useMemo(() => preview?.step_numbers ?? {}, [preview]);
  const issueMap = useMemo(() => {
    const m: Record<string, string[]> = {};
    for (const i of issues) if (i.node_id) (m[i.node_id] ??= []).push(i.message);
    return m;
  }, [issues]);
  const fresh = useMemo(() => {
    const last = [...(wf?.chat ?? [])].reverse().find((turn) => turn.role === 'assistant');
    const m: Record<string, 'added' | 'changed'> = {};
    for (const id of last?.changed ?? []) m[id] = 'changed';
    for (const id of last?.added ?? []) m[id] = 'added';
    return m;
  }, [wf]);

  const send = async (text: string) => {
    if (!wf) return;
    setBusy('chat');
    setChatError(null);
    try {
      const res = built ? await agentBuilderApi.generate(name, wf.requirement, text) : await agentBuilderApi.generate(name, text);
      setSelected(null);
      adopt(res.workflow);
    } catch (e) {
      const status = e instanceof BuilderApiError ? e.status : -1;
      setChatError(
        status === 503
          ? t("The agent platform is unavailable, so I can't change the workflow right now. Please try again in a moment.")
          : status === 0
            ? t('The agent builder service is not reachable. Please try again in a moment.')
            : (e as Error).message,
      );
      // The service records failed requests in the chat; show its record instead.
      agentBuilderApi.workflow(name).then((w) => {
        adopt(w);
        if (w.chat.at(-1)?.error) setChatError(null);
      }, () => undefined);
    } finally {
      setBusy(null);
    }
  };

  // Back to the workflow as it was before the builder's last change; the chat records it.
  const undo = async () => {
    if (!wf) return;
    setBusy('chat');
    setChatError(null);
    try {
      const res = await agentBuilderApi.undo(name);
      setSelected(null);
      adopt(res.workflow);
    } catch (e) {
      setChatError((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  // Throw away the unpublished changes and show the saved workflow again.
  const discard = async () => {
    if (!wf) return;
    setBusy('chat');
    try {
      const res = await agentBuilderApi.discard(name);
      setSelected(null);
      adopt(res.workflow);
      CtyShowToast(checkType.success, t('Changes discarded. This is the saved workflow.'));
    } catch (e) {
      CtyShowToast(checkType.error, (e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const publish = async () => {
    if (!wf) return;
    setBusy('publish');
    try {
      adopt(await agentBuilderApi.publish(name, wf));
      CtyShowToast(checkType.success, t('Published. The agent now follows this workflow.'));
    } catch (e) {
      if (e instanceof BuilderApiError && e.issues.length) setIssues(e.issues);
      CtyShowToast(checkType.error, (e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const saveSettings = async (s: Settings) => {
    if (!wf) return;
    setBusy('save');
    try {
      const res = await agentBuilderApi.saveDraft(name, { ...wf, settings: s });
      adopt(res.workflow);
      settingsDialog.onClose();
      CtyShowToast(checkType.success, t('Settings saved.'));
    } catch (e) {
      CtyShowToast(checkType.error, (e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  // Renames the journey (its title only): the instructions and the published state do not change.
  const rename = async () => {
    const title = renaming?.trim();
    setRenaming(null);
    if (!wf || !title || title === wf.settings.title) return;
    try {
      setWf(await agentBuilderApi.rename(name, title));
    } catch (e) {
      CtyShowToast(checkType.error, (e as Error).message);
    }
  };

  const remove = async () => {
    setBusy('save');
    try {
      await agentBuilderApi.deleteAgent(name);
      CtyShowToast(checkType.success, t('Journey deleted.'));
      navigate(backPath);
    } catch (e) {
      CtyShowToast(checkType.error, (e as Error).message);
    } finally {
      setBusy(null);
      deleteDialog.onClose();
    }
  };

  if (loadError && !wf) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 p-6 text-center">
        <p className="text-danger">{loadError}</p>
        <CtyButton variant="flat" onPress={() => navigate(backPath)}>
          {t('Back to journeys')}
        </CtyButton>
      </div>
    );
  }
  if (!wf) return <div className="p-6 text-default-500">{t('Loading…')}</div>;

  const status = !built
    ? { color: 'default' as const, text: t('Not built yet') }
    : !sync // the status check failed (e.g. the agent platform is unavailable): don't guess
      ? { color: 'default' as const, text: t('Status unavailable') }
      : sync.requirement === 'changed'
      ? { color: 'warning' as const, text: t('Requirement changed: send a message to rebuild') }
      : sync.instructions === 'in_sync'
        ? { color: 'success' as const, text: t('Published') }
        : sync.instructions === 'behind'
          ? { color: 'warning' as const, text: t('Unpublished changes') }
          : { color: 'default' as const, text: t('Not published') };

  return (
    <div className="asj-builder-page flex h-full flex-col" data-testid="agent-builder-page">
      {/* One slim bar: what this is and its state on the left, the actions on the right. */}
      <div className="flex items-center gap-2 border-b border-default-200 bg-content1 px-2 py-1.5">
        <CtyButton isIconOnly size="sm" variant="light" onPress={() => navigate(backPath)} title={t('Back to journeys')}>
          <Icon icon="lucide:arrow-left" width={18} />
        </CtyButton>
        <CtyButton isIconOnly size="sm" variant="light" onPress={() => setPanelOpen(!panelOpen)} title={panelOpen ? t('Hide the chat') : t('Show the chat')} id="ab-toggle-chat">
          <Icon icon={panelOpen ? 'lucide:panel-left-close' : 'lucide:panel-left-open'} width={18} />
        </CtyButton>
        {renaming !== null ? (
          <Input
            size="sm"
            autoFocus
            aria-label={t('Journey name')}
            className="w-72"
            value={renaming}
            onValueChange={setRenaming}
            onBlur={() => void rename()}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void rename();
              if (e.key === 'Escape') setRenaming(null);
            }}
            id="ab-rename-input"
          />
        ) : (
          <Tooltip content={name} placement="bottom">
            <h1 className="flex min-w-0 items-center gap-0.5 text-base font-semibold" data-testid="ab-title">
              <span className="truncate">{wf.settings.title || t('New agent')}</span>
              <CtyButton isIconOnly size="sm" variant="light" onPress={() => setRenaming(wf.settings.title)} title={t('Rename')} id="ab-rename">
                <Icon icon="lucide:pencil" width={13} className="text-default-400" />
              </CtyButton>
            </h1>
          </Tooltip>
        )}
        {wf.unpublished && (
          <Tooltip content={t('The changes on the canvas are not saved yet. Publish to keep them, or discard them from Actions.')} placement="bottom">
            <Chip size="sm" variant="flat" color="warning" data-testid="ab-unpublished">
              {t('Unpublished changes')}
            </Chip>
          </Tooltip>
        )}
        {sync?.hand_edited && (
          <Chip size="sm" variant="flat" color="danger">
            {t('Files edited by hand')}
          </Chip>
        )}
        <div className="ml-auto flex items-center gap-2">
          <Dropdown placement="bottom-end">
            <DropdownTrigger>
              <Button size="sm" variant="flat" id="ab-actions" endContent={<Icon icon="lucide:chevron-down" width={14} />}>
                {t('Actions')}
              </Button>
            </DropdownTrigger>
            <DropdownMenu
              aria-label={t('Journey actions')}
              disabledKeys={[...(!built ? ['instructions'] : []), ...(!sync || sync.instructions === 'not_published' ? ['run'] : []), ...(!wf.unpublished || busy !== null ? ['discard'] : [])]}
              onAction={(key) => {
                if (key === 'instructions') instructions.onOpen();
                if (key === 'run') runDialog.onOpen();
                if (key === 'settings') settingsDialog.onOpen();
                if (key === 'actions') actionsDialog.onOpen();
                if (key === 'discard') void discard();
                if (key === 'delete') deleteDialog.onOpen();
              }}
            >
              <DropdownItem key="run" startContent={<Icon icon="lucide:play" width={16} />} description={t('Run it on one email, live')} id="ab-test-run">
                {t('Test run')}
              </DropdownItem>
              <DropdownItem key="instructions" startContent={<Icon icon="lucide:file-text" width={16} />} description={t('What the agent follows')} id="ab-open-instructions">
                {t('Instructions')}
              </DropdownItem>
              <DropdownItem key="actions" startContent={<Icon icon="lucide:wrench" width={16} />} description={t('{{count}} actions over MCP', { count: tools.length })} id="ab-open-actions">
                {t('Available actions')}
              </DropdownItem>
              <DropdownItem key="settings" startContent={<Icon icon="lucide:settings" width={16} />} description={t('Model, step limit, sample input')} id="ab-open-settings">
                {t('Settings')}
              </DropdownItem>
              <DropdownItem key="discard" startContent={<Icon icon="lucide:undo-2" width={16} />} description={t('Back to the saved workflow')} id="ab-discard">
                {t('Discard changes')}
              </DropdownItem>
              <DropdownItem key="delete" className="text-danger" color="danger" startContent={<Icon icon="lucide:trash-2" width={16} />} id="ab-delete">
                {t('Delete journey')}
              </DropdownItem>
            </DropdownMenu>
          </Dropdown>
          <CtyButton
            size="sm"
            color="primary"
            onPress={publish}
            isDisabled={!built || issues.length > 0 || busy !== null || sync?.instructions === 'in_sync'}
            isLoading={busy === 'publish'}
            id="ab-publish"
            data-testid="ab-status"
            tooltipContent={!built ? t('Build the workflow first.') : issues.length ? t('Fix the problems first.') : status.text}
          >
            {sync?.instructions === 'in_sync' ? t('Published') : sync?.instructions === 'behind' ? t('Publish changes') : t('Publish')}
          </CtyButton>
        </div>
      </div>

      <div className="grid min-h-0 flex-1" style={{ gridTemplateColumns: panelOpen ? 'clamp(260px, 24%, 380px) minmax(0, 1fr)' : 'minmax(0, 1fr)' }}>
        {panelOpen && (
          <div className="flex min-h-0 flex-col border-r border-default-200" data-testid="ab-side-panel">
            <BuilderChat turns={wf.chat ?? []} built={built} busy={busy === 'chat'} error={chatError} blueprint={blueprint} onSend={send} onUndo={undo} />
          </div>
        )}
        <div className="flex min-h-0 flex-col">
          {catalogueError && (
            <div className="border-b border-warning bg-warning/10 px-4 py-1.5 text-sm text-warning-700" role="alert" data-testid="ab-catalogue-warning">
              {t("The agent platform's tool list is unavailable, so the workflow can't be built or changed right now. The agent below is unchanged.")}
            </div>
          )}
          {issues.length > 0 && (
            <div className="border-b border-danger-200 bg-danger-50 text-sm text-danger" data-testid="ab-issues">
              <button type="button" className="flex w-full items-center gap-2 px-3 py-1.5 text-left" onClick={() => setIssuesOpen(!issuesOpen)} aria-expanded={issuesOpen}>
                <Icon icon="lucide:triangle-alert" width={16} />
                <span className="flex-1 truncate">
                  {issues.length === 1 ? issues[0].message : t('{{count}} problems to fix before publishing', { count: issues.length })}
                </span>
                {issues.length > 1 && <Icon icon={issuesOpen ? 'lucide:chevron-up' : 'lucide:chevron-down'} width={16} />}
              </button>
              {issuesOpen && issues.length > 1 && (
                <ul className="max-h-40 list-disc overflow-y-auto px-8 pb-2">
                  {issues.map((i, k) => (
                    <li key={k}>
                      {i.node_id ? (
                        <button className="text-left underline" onClick={() => setSelected(i.node_id)}>
                          {i.message}
                        </button>
                      ) : (
                        i.message
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
          <div className="relative min-h-0 flex-1" data-testid="ab-canvas">
            {built ? (
              <>
                <BuilderFlow workflow={wf} tools={tools} issues={issueMap} steps={steps} fresh={fresh} selected={selected} onSelect={setSelected} />
                {selectedNode && (
                  <NodeDetails
                    node={selectedNode}
                    step={steps[selectedNode.id]}
                    tool={tools.find((x) => x.name === selectedNode.data.tool)}
                    issues={issueMap[selectedNode.id] ?? []}
                    onClose={() => setSelected(null)}
                  />
                )}
              </>
            ) : (
              <div className="flex h-full flex-col items-center justify-center gap-1 text-center">
                <p className="text-default-700">{t('The workflow appears here.')}</p>
                <p className="text-sm text-default-500">{t('Describe what this journey should do in the chat on the left.')}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      <Modal isOpen={instructions.isOpen} onOpenChange={instructions.onOpenChange} size="4xl" scrollBehavior="inside">
        <ModalContent>
          <ModalHeader>{t('Agent instructions')}</ModalHeader>
          <ModalBody className="pb-6">
            <p className="text-sm text-default-500">
              {t('Steps and Agent are compiled from the workflow.')}{' '}
              {sync?.instructions === 'in_sync' ? t('The published agent uses exactly this.') : t('Publish to make the agent use them.')}
            </p>
            <Tabs aria-label={t('Agent instructions')}>
              <Tab key="in-use" title={<TabTitle label={t('In use now')} info={t(TAB_INFO.inUse)} />}>
                <div className="flex flex-col gap-3" data-testid="ab-instructions-in-use">
                  <p className="text-xs text-default-500">
                    {sync?.instructions === 'in_sync'
                      ? t('The published instructions: the same as the workflow compiles to.')
                      : t('The published instructions the agent runs with now. They differ from the workflow until you publish.')}
                  </p>
                  {inUse ? (
                    <>
                      <h4 className="text-sm font-semibold">{t('Steps (task.md)')}</h4>
                      <pre className="whitespace-pre-wrap rounded bg-default-100 p-2 text-xs">{inUse.task_md}</pre>
                      <h4 className="text-sm font-semibold">{t('Agent (agent.md)')}</h4>
                      <pre className="whitespace-pre-wrap rounded bg-default-100 p-2 text-xs">{inUse.agent_md}</pre>
                    </>
                  ) : (
                    <p className="text-sm text-default-500">{t('Not published yet.')}</p>
                  )}
                </div>
              </Tab>
              <Tab key="task" title={<TabTitle label={t('Steps (task.md)')} info={t(TAB_INFO.steps)} />}>
                <InstructionLines text={preview?.task_md ?? ''} steps={steps} selected={selected} onSelect={(id) => { instructions.onClose(); setSelected(id); }} />
              </Tab>
              <Tab key="agent" title={<TabTitle label={t('Agent (agent.md)')} info={t(TAB_INFO.agent)} />}>
                <pre className="whitespace-pre-wrap text-xs">{preview?.agent_md}</pre>
              </Tab>
              {blueprint && (
                <Tab key="blueprint" title={<TabTitle label={t('Blueprint')} info={t(TAB_INFO.blueprint)} />}>
                  <div className="flex flex-col gap-3 text-sm" data-testid="ab-instructions-blueprint">
                    <p className="text-xs text-default-500">
                      {t('Every {{title}} journey follows these, whatever its workflow says.', { title: blueprint.title })}
                    </p>
                    <h4 className="font-semibold">{t('Instructions')}</h4>
                    <pre className="whitespace-pre-wrap rounded bg-default-100 p-2 text-xs">{blueprint.instructions}</pre>
                    <h4 className="font-semibold">{t('Rules')}</h4>
                    <ul className="list-disc pl-5 text-xs">
                      {Object.entries(blueprint.rules.first_tool ?? {}).map(([trigger, tool]) => (
                        <li key={`first-${trigger}`}>{t('When started by {{trigger}}, the first action is {{tool}}.', { trigger, tool })}</li>
                      ))}
                      {Object.entries(blueprint.rules.before ?? {}).map(([tool, needs]) => (
                        <li key={`before-${tool}`}>{t('{{tool}} only after {{needs}}.', { tool, needs: needs.join(', ') })}</li>
                      ))}
                      {(blueprint.rules.at_most_once ?? []).map((tool) => (
                        <li key={`once-${tool}`}>{t('{{tool}} at most once per run.', { tool })}</li>
                      ))}
                    </ul>
                    <h4 className="font-semibold">{t('Outcomes')}</h4>
                    <ul className="list-disc pl-5 text-xs">
                      {Object.entries(blueprint.outcomes).map(([key, text]) => (
                        <li key={key}>
                          <span className="font-mono">{key}</span>: {text}
                        </li>
                      ))}
                    </ul>
                  </div>
                </Tab>
              )}
              <Tab key="requirement" title={<TabTitle label={t('Requirement')} info={t(TAB_INFO.requirement)} />}>
                <p className="whitespace-pre-wrap text-sm" data-testid="ab-requirement">{wf.requirement || t('Nothing yet.')}</p>
              </Tab>
            </Tabs>
          </ModalBody>
        </ModalContent>
      </Modal>

      <SimpleDeleteConfirmationModal
        isOpen={deleteDialog.isOpen}
        onOpenChange={deleteDialog.onOpenChange}
        entityName={wf.settings.title || name}
        onConfirm={remove}
        isConfirmLoading={busy === 'save'}
        cancelLabel={t('Close')}
        confirmLabel={t('Delete')}
        confirmButtonTestId="ab-delete-confirm"
      />

      <Modal isOpen={actionsDialog.isOpen} onOpenChange={actionsDialog.onOpenChange} size="2xl" scrollBehavior="inside">
        <ModalContent>
          <ModalHeader>{t('Available actions')}</ModalHeader>
          <ModalBody className="pb-6">
            <p className="text-sm text-default-500">{t('The only actions this journey can use. Each runs on an MCP server; the builder never uses anything else.')}</p>
            <ToolsPanel blueprint={blueprint} tools={tools} alwaysOpen />
          </ModalBody>
        </ModalContent>
      </Modal>

      <RunDialog
        name={name}
        isOpen={runDialog.isOpen}
        onOpenChange={runDialog.onOpenChange}
        tools={tools}
        behind={sync?.instructions === 'behind' || sync?.requirement === 'changed'}
      />

      <SettingsModal
        isOpen={settingsDialog.isOpen}
        onOpenChange={settingsDialog.onOpenChange}
        settings={wf.settings}
        profiles={profiles}
        busy={busy === 'save'}
        onSave={saveSettings}
      />
    </div>
  );
}

// What each Instructions tab is and how it affects the agent (the info icon on the tab).
const TAB_INFO = {
  inUse:
    'What the agent runs with right now: the published agent.md and task.md. It changes only when you publish. If it differs from Steps and Agent, the agent still follows this.',
  steps:
    "The task the agent gets on every run: the workflow's steps in order and the outcome it must report. Compiled from the workflow; change it in the chat, then publish. It decides what the agent does and in which order.",
  agent:
    "Who the agent is on every step: role, goal, rules, the actions it may use, the model and its step limit. Compiled from the workflow, Settings and the blueprint. It shapes the agent's judgement and tone.",
  blueprint:
    'Fixed for every journey of this kind, not edited here. Its instructions are copied into agent.md, the agent can only use its actions, and its rules are checked when the workflow is built.',
  requirement:
    'What you asked for, in plain words. The builder builds the workflow from it; the agent never reads it. Change it through the chat.',
};

function TabTitle({ label, info }: { label: string; info: string }) {
  return (
    <span className="flex items-center gap-1">
      {label}
      <Tooltip content={<p className="max-w-xs p-1 text-xs">{info}</p>} placement="bottom" showArrow delay={150}>
        <span aria-label={info} className="inline-flex text-default-400 hover:text-default-600">
          <Icon icon="lucide:info" width={13} />
        </span>
      </Tooltip>
    </span>
  );
}

function InstructionLines({ text, steps, selected, onSelect }: { text: string; steps: Record<string, number>; selected: string | null; onSelect: (id: string) => void }) {
  const byNumber = Object.fromEntries(Object.entries(steps).map(([id, n]) => [n, id]));
  const active = selected ? steps[selected] : undefined;
  const body = text.replace(/^---[\s\S]*?\n---\n/, '');
  return (
    <ol className="text-sm" data-testid="ab-instructions">
      {body.split('\n').map((line, i) => {
        const m = /^(\d+)\. /.exec(line);
        const nodeId = m ? byNumber[Number(m[1])] : undefined;
        const current = m && Number(m[1]) === active ? 'bg-primary/20' : '';
        return (
          <li key={i} className={`whitespace-pre-wrap rounded ${current}`}>
            {nodeId ? (
              // A numbered step: a button, so it can be selected with the keyboard too.
              <button type="button" className="w-full cursor-pointer whitespace-pre-wrap rounded px-2 py-0.5 text-left hover:bg-primary/10" onClick={() => onSelect(nodeId)}>
                {line}
              </button>
            ) : (
              <span className="block px-2 py-0.5">{line || ' '}</span>
            )}
          </li>
        );
      })}
    </ol>
  );
}

function SettingsModal({
  isOpen,
  onOpenChange,
  settings,
  profiles,
  busy,
  onSave,
}: {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  settings: Settings;
  profiles: string[];
  busy: boolean;
  onSave: (s: Settings) => void;
}) {
  const { t } = useTranslation();
  const [s, setS] = useState(settings);
  useEffect(() => {
    if (isOpen) setS(settings);
  }, [isOpen, settings]);
  const sample = Object.entries(s.sample_inputs ?? {})
    .map(([k, v]) => `${k}=${v}`)
    .join('\n');
  return (
    <Modal isOpen={isOpen} onOpenChange={onOpenChange} size="2xl" scrollBehavior="inside">
      <ModalContent>
        <ModalHeader>{t('Agent settings')}</ModalHeader>
        <ModalBody className="pb-6">
          <p className="text-sm text-default-500">{t('Role, goal and rules come from the chat. The rest are technical settings.')}</p>
          <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-sm">
            <dt className="text-default-500">{t('Role')}</dt>
            <dd>{s.role || '—'}</dd>
            <dt className="text-default-500">{t('Goal')}</dt>
            <dd>{s.goal || '—'}</dd>
            <dt className="text-default-500">{t('Rules')}</dt>
            <dd className="max-h-48 overflow-y-auto whitespace-pre-wrap rounded bg-default-100 p-2 text-xs">{s.rules || '—'}</dd>
            <dt className="text-default-500">{t('Outcome format')}</dt>
            <dd>{s.output_model}</dd>
          </dl>
          <Select label={t('LLM profile')} selectedKeys={[s.llm]} onSelectionChange={(keys) => setS({ ...s, llm: String(Array.from(keys)[0] ?? s.llm) })}>
            {profiles.map((p) => (
              <SelectItem key={p}>{p}</SelectItem>
            ))}
          </Select>
          <Input type="number" label={t('Max steps')} min={1} max={50} value={String(s.max_steps)} onValueChange={(v) => setS({ ...s, max_steps: Number(v) })} />
          <Input
            label={t('Sample input for a test run (key=value)')}
            value={sample}
            onValueChange={(v) =>
              setS({
                ...s,
                sample_inputs: Object.fromEntries(
                  v
                    .split(/[\n,]/)
                    .map((l) => l.split('='))
                    .filter((p) => p[0]?.trim())
                    .map(([k, ...rest]) => [k.trim(), rest.join('=').trim()]),
                ),
              })
            }
          />
          <div className="flex justify-end">
            <CtyButton color="primary" onPress={() => onSave(s)} isLoading={busy} isDisabled={JSON.stringify(s) === JSON.stringify(settings)}>
              {t('Save settings')}
            </CtyButton>
          </div>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}

export default AgentBuilderPage;
